import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import StaffPageShell from '../components/StaffPageShell';
import { ThemeButton } from '../components/ThemeWrapper';
import usePermissions from '../hooks/usePermissions';
import useCreationRequestQueue from '../hooks/useCreationRequestQueue';
import { labelForRequestedRole } from '../domain/userCreationRequest/requestRoles';
import { onboardingReviewRows } from '../domain/userCreationRequest/onboardingDisplay';

const STATUSES = [
  { id: 'pending', label: 'Pending' },
  { id: 'approved', label: 'Approved' },
  { id: 'rejected', label: 'Rejected' },
];

export default function AdminCreationRequestsPage() {
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const queue = useCreationRequestQueue();

  useEffect(() => {
    if (!hasPermission('users.requests.review')) {
      navigate('/dashboard');
    }
  }, [hasPermission, navigate]);

  if (!hasPermission('users.requests.review')) return null;

  const payloadRows = onboardingReviewRows(queue.selected);

  return (
    <StaffPageShell
      title="Account requests"
      subtitle="VE onboarding requests wait here until you create the login"
      wide
      actions={
        <>
          <ThemeButton type="button" variant="outline" onClick={() => navigate('/admin/onboarding')}>
            New onboarding
          </ThemeButton>
          <ThemeButton type="button" onClick={() => queue.load()} disabled={queue.loading}>
            Refresh
          </ThemeButton>
        </>
      }
    >
      {queue.error && (
        <div className="mb-4 p-3 rounded text-sm" style={{ backgroundColor: 'var(--color-error-100)', color: 'var(--color-error)' }}>
          {queue.error}
        </div>
      )}
      {queue.message && (
        <div className="mb-4 p-3 rounded text-sm theme-card theme-text">{queue.message}</div>
      )}
      {queue.approvedSecret && (
        <div className="mb-4 p-3 rounded text-sm theme-card">
          <p className="theme-text font-medium">Temporary password (shown once)</p>
          <code className="block mt-2 text-base">{queue.approvedSecret}</code>
        </div>
      )}

      <div className="flex gap-2 mb-4">
        {STATUSES.map((s) => (
          <button
            key={s.id}
            type="button"
            className={`px-3 py-1 rounded ${queue.status === s.id ? 'theme-text-gradient' : 'theme-text-secondary'}`}
            style={
              queue.status === s.id
                ? { borderBottom: '2px solid var(--color-primary)' }
                : {}
            }
            onClick={() => {
              queue.setStatus(s.id);
              queue.setSelectedId(null);
            }}
          >
            {s.label}
          </button>
        ))}
        <span className="text-sm theme-text-secondary self-center">{queue.total} total</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-1 rounded-lg p-3 max-h-[75vh] overflow-y-auto border" style={{ borderColor: 'var(--color-border)' }}>
          {queue.loading ? (
            <p className="theme-text-secondary text-sm">Loading…</p>
          ) : queue.items.length === 0 ? (
            <p className="theme-text-secondary text-sm">No {queue.status} requests.</p>
          ) : (
            <ul className="space-y-2">
              {queue.items.map((row) => (
                <li key={row.id}>
                  <button
                    type="button"
                    className="w-full text-left p-3 rounded-lg border"
                    style={{
                      borderColor:
                        queue.selectedId === row.id
                          ? 'var(--color-primary)'
                          : 'var(--color-border)',
                    }}
                    onClick={() => queue.setSelectedId(row.id)}
                  >
                    <span className="block font-medium theme-text">
                      {row.firstName} {row.lastName}
                    </span>
                    <span className="block text-xs theme-text-secondary">{row.email}</span>
                    <span className="block text-xs theme-text-secondary">
                      {labelForRequestedRole(row.requestedRole)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="lg:col-span-2 rounded-lg p-4 border" style={{ borderColor: 'var(--color-border)' }}>
          {!queue.selected ? (
            <p className="theme-text-secondary">Select a request to review.</p>
          ) : (
            <>
              <h2 className="text-lg font-semibold theme-text mb-3">
                {queue.selected.firstName} {queue.selected.lastName}
              </h2>
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm mb-4">
                {payloadRows.map((row) => (
                  <div key={row.label} className={row.label === 'Notes' || row.label.startsWith('Labs need') ? 'md:col-span-2' : ''}>
                    <dt className="theme-text-secondary">{row.label}</dt>
                    <dd className="theme-text whitespace-pre-wrap">{row.value}</dd>
                  </div>
                ))}
              </dl>

              {queue.selected.status === 'pending' && (
                <>
                  <label className="block text-sm theme-text mb-2" htmlFor="review-note">
                    Review note (optional)
                  </label>
                  <textarea
                    id="review-note"
                    className="w-full px-3 py-2 mb-3 theme-input rounded-lg"
                    rows={3}
                    value={queue.reviewNote}
                    onChange={(e) => queue.setReviewNote(e.target.value)}
                  />
                  <div className="flex gap-2">
                    <ThemeButton type="button" onClick={queue.approve} disabled={queue.busy}>
                      Approve and create account
                    </ThemeButton>
                    <ThemeButton
                      type="button"
                      variant="secondary"
                      onClick={queue.reject}
                      disabled={queue.busy}
                    >
                      Reject
                    </ThemeButton>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </StaffPageShell>
  );
}
